import { Injectable } from '@angular/core';
import { GqlService, SortOrder } from '@services/gql.service';
import { GenericOperator } from '@services/operator.class';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { take } from 'rxjs/operators';
import { MainQuery } from 'src/app/layouts/main-layout/state/main.query';
import {
  TrialInsightsResponseGeneric,
  TrialInsightsScreenFailInputType,
  TrialInsightsScreenFailResponseType,
  TrialInsightsState,
} from '../../models/trial-insights-store.model';
import { TrialInsightsStore } from '../../store/trial-insights.store';

type state = Partial<TrialInsightsState['screenFail']>;
type requestInput = TrialInsightsScreenFailInputType;
type requestType = 'initial' | 'remaining';
type loadingState = requestType | 'completed';
type responseType = TrialInsightsScreenFailResponseType;
type response = TrialInsightsResponseGeneric<responseType>[];

@Injectable()
export class TrialInsightsClinicalScreenFailStoreService {
  constructor(
    private store: TrialInsightsStore,
    private mainQuery: MainQuery,
    private gqlService: GqlService
  ) {}

  getScreenFailSummary$(): Observable<any> {
    return this.mainQuery
      .select('trialKey')
      .pipe(
        this.updateLoadingState('initial'),
        this.request('initial'),
        this.processResponse(),
        this.updateLoadingState('remaining'),
        this.request('remaining'),
        this.processResponse(),
        this.updateLoadingState('completed')
      );
  }

  requestInput(type: requestType): requestInput[] {
    const request = [
      {
        site_screen_fail_rates_order: SortOrder.DESC,
        site_screen_fail_totals_order: SortOrder.DESC,
      },
    ];

    if (type === 'remaining') {
      request[0].site_screen_fail_rates_order = SortOrder.ASC;
      request[0].site_screen_fail_totals_order = SortOrder.ASC;
    }

    return request;
  }

  loadingState(type: loadingState): state {
    const loadingState: state = {};

    if (type === 'initial') {
      loadingState.isLoading = true;
      loadingState.isLoadingRemaining = true;
      loadingState.data = null;
    } else if (type === 'remaining') {
      loadingState.isLoading = false;
    } else {
      loadingState.isLoadingRemaining = false;
    }

    return loadingState;
  }

  updateLoadingState(type: loadingState) {
    const updates = this.loadingState(type);

    const processFn = (sourceValue: any, subscriber: Subscriber<any>) => {
      this.store.update((state) => {
        return {
          ...state,
          screenFail: {
            ...state.screenFail,
            ...updates,
          },
        };
      });

      subscriber.next(updates);
    };

    const operatorConfig = new GenericOperator(processFn);
    return operatorConfig.operator();
  }

  request(type: requestType) {
    const processFn = (sourceValue: any, subscriber: Subscriber<response>) => {
      const inputs = this.requestInput(type);
      const input$ = inputs.map((input) => this.gqlService.getScreenFailSummary$(input));

      forkJoin(input$)
        .pipe(take(1))
        .subscribe((results) => {
          subscriber.next(results);
        });
    };

    const operatorConfig = new GenericOperator(processFn);
    return operatorConfig.operator();
  }

  processResponse() {
    const processFn = (sourceValue: response, subscriber: Subscriber<any>) => {
      if (!sourceValue.length) {
        return subscriber.next(sourceValue);
      }

      const stateUpdates = sourceValue.reduce((updates, update) => {
        if (!update.data) {
          return updates;
        }

        updates.push(update.data);
        return updates;
      }, [] as responseType[]);

      this.store.update((state) => {
        const currentData = state.screenFail.data || [];

        return {
          ...state,
          screenFail: {
            ...state.screenFail,
            data: [...currentData, ...stateUpdates],
          },
        };
      });

      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);
    return operatorConfig.operator();
  }
}
